import { useEffect, useRef, type ReactNode } from 'react';

/**
 * The "⋯" menu of a page header: the rarely used actions behind one button. A plain
 * `<details>`, so the keyboard opens it natively (Enter or Space on the summary); this adds
 * what the element lacks: Escape and a click outside close it and give the focus back to the
 * button, opening it moves the focus onto the first action, and choosing an action closes it.
 */
export function OverflowMenu({ label, children }: { label: string; children: ReactNode }) {
  const root = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return undefined;
    function onPointer(e: MouseEvent) {
      if (el && el.open && !el.contains(e.target as Node)) el.open = false;
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape' || !el || !el.open) return;
      el.open = false;
      el.querySelector('summary')?.focus();
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  return (
    <details
      className="overflow-menu"
      ref={root}
      onToggle={(e) => {
        if (e.currentTarget.open)
          e.currentTarget
            .querySelector<HTMLElement>('.overflow-items a, .overflow-items button')
            ?.focus();
      }}
    >
      <summary className="button ghost" aria-label={label} title={label}>
        <i className="bi bi-three-dots" aria-hidden="true" />
      </summary>
      <div
        className="overflow-items"
        onClick={(e) => {
          if (root.current && (e.target as HTMLElement).closest('a, button'))
            root.current.open = false;
        }}
      >
        {children}
      </div>
    </details>
  );
}
